import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import cx from "clsx"
import type { TemplateModule } from "shared"
import { AdminButton } from "@rawa/ui/AdminButton"
import { AdminDialog } from "./AdminDialog"
import { ModuleForm } from "@rawa/form-modules/ModuleForm"
import { fetchFormModuleVersion } from "../api"
import { exampleValues } from "../utils/boundFields"
import type { FieldValues } from "@rawa/form-modules/fieldValues"
import { Tag } from "@rawa/ui/Tag"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./SessionTemplatePreviewDialog.module.css"

/**
 * Shows a template as a practitioner will fill it in on a session note.
 *
 * Open for as long as it is mounted, and shows the template as edited, saved
 * or not. The form can be tried out, and what is typed lives only that long.
 */
export function SessionTemplatePreviewDialog({
  onClose,
  name,
  description,
  programmes,
  modules,
}: {
  onClose: () => void
  name: string
  description: string
  /** The names of the programmes the template is for. */
  programmes: string[]
  /** In the template's order. */
  modules: TemplateModule[]
}) {
  const [values, setValues] = useState<FieldValues>({})

  return (
    <AdminDialog
      open
      onClose={onClose}
      labelledBy="template-preview-title"
      className={styles.dialog}
    >
      <h2
        id="template-preview-title"
        className={cx(typography.h5, styles.title)}
      >
        {name} preview
      </h2>
      <div className={styles.template}>
        <div className={styles.tags}>
          <Tag colour="aqua">Session template</Tag>
          {programmes.map((programme) => (
            <Tag key={programme} colour="aqua">
              {programme}
            </Tag>
          ))}
        </div>
        <h3 className={styles.name}>{name}</h3>
        <p className={styles.description}>{description}</p>
        {modules.length === 0 && (
          <p className={styles.empty}>This template has no modules yet.</p>
        )}
        {modules.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            values={values}
            onChange={(id, value) =>
              setValues((held) => ({ ...held, [id]: value }))
            }
          />
        ))}
      </div>
      <div className={styles.actions}>
        <AdminButton onClick={onClose}>Close preview</AdminButton>
      </div>
    </AdminDialog>
  )
}

/**
 * One module's card: its name and description, and its fields once they are
 * read. A template holds modules by reference, so each card reads the version
 * it holds. Its bound fields start at their bindings' examples:
 * a changeable one can be changed, a display-only one is read-only.
 */
function ModuleCard({
  module,
  values,
  onChange,
}: {
  module: TemplateModule
  values: FieldValues
  onChange: Parameters<typeof ModuleForm>[0]["onChange"]
}) {
  // The version the template holds, never the working copy (ADR-040).
  const { data: held, isError } = useQuery({
    queryKey: ["form-module", module.id, "versions", module.version],
    queryFn: ({ signal }) =>
      fetchFormModuleVersion(module.id, module.version, signal),
    // A published version never changes, so it is read once.
    staleTime: Infinity,
    // An administrator at a desk is told what went wrong rather than kept
    // waiting through the client's retries.
    retry: false,
    meta: { source: "fetchFormModuleVersion" },
  })
  const headingId = `preview-module-${module.id}`
  const fields = held?.definition.fields ?? []

  return (
    <section className={styles.module} aria-labelledby={headingId}>
      <h4 id={headingId} className={styles.moduleName}>
        {module.name}
      </h4>
      <p className={styles.description}>{module.description}</p>
      {isError && (
        <p className={styles.loadError}>Could not load this module's fields.</p>
      )}
      {held && (
        <ModuleForm
          fields={fields}
          values={{ ...exampleValues(fields), ...values }}
          onChange={onChange}
        />
      )}
    </section>
  )
}
